import {
	isStreamResponse,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import type { ChatStoreContainerProps } from './types';

type LookupResult =
	| { outcome: 'recovered'; response: StreamResponse }
	| {
			outcome:
				| 'superseded'
				| 'missing'
				| 'inProgress'
				| 'failed'
				| 'cancelled'
				| 'unknownState'
				| 'lookupFailed';
	  };

/** One history read, with no prompt submission, polling, or automatic reconnect. */
export const lookupCompletedReplayMessage = async ({
	conversationId,
	messageId,
	getChat,
	capabilities,
	isCurrent,
}: {
	conversationId: string;
	messageId: string;
	getChat: ChatStoreContainerProps['assistanceService']['getChat'];
	capabilities: ChatStoreContainerProps['capabilities'];
	isCurrent: () => boolean;
}): Promise<LookupResult> => {
	if (!isCurrent()) {
		return { outcome: 'superseded' };
	}
	let history: Awaited<ReturnType<typeof getChat>>;
	try {
		history = await getChat(conversationId, capabilities);
	} catch {
		return { outcome: isCurrent() ? 'lookupFailed' : 'superseded' };
	}
	if (!isCurrent()) {
		return { outcome: 'superseded' };
	}
	const response = history.find(
		(message): message is StreamResponse =>
			isStreamResponse(message) && message.message.message.id === messageId,
	);
	if (!response) {
		return { outcome: 'missing' };
	}
	switch (response.message.message.async_status) {
		case 'SUCCESSFUL':
			return { outcome: 'recovered', response };
		case 'IN_PROGRESS':
			return { outcome: 'inProgress' };
		case 'FAILED':
			return { outcome: 'failed' };
		case 'CANCELLED':
			return { outcome: 'cancelled' };
		default:
			return { outcome: 'unknownState' };
	}
};
